import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { getAddressStringByLatLong, getTimeZoneByLocation } from "../Actions/companyActions";
import isEmpty from "./is_empty";
import { showToastNotification } from "./showToastNotification";

export interface LocationData {
  city: string;
  state: string;
  zip: string;
  street_number: string;
  street: string;
  address_line: string;
  longitude: number;
  latitude: number;
  time_zone: string;
  formatted_address: string;
}

const user = JSON.parse(localStorage.getItem("user") || "{}");

// Ensure Google Maps JS API is ready before geocoding
async function waitForGoogleMaps(timeoutMs: number = 8000, pollMs: number = 100): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const check = () => {
      // Check for Google Maps and Places library (required for geocoding)
      if (window.google?.maps?.places) {
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error("Google Maps API not loaded in time"));
        return;
      }
      setTimeout(check, pollMs);
    };
    check();
  });
}

const getAddressStringData = async (address: string): Promise<LocationData | null> => {
  try {
    // Wait for Google Maps API to be available (handles URL-driven early calls)
    await waitForGoogleMaps();

    // Clean and normalize the address
    const cleanedAddress = address.trim().replace(/\s+/g, ' ');
    
    const results = await geocodeByAddress(cleanedAddress);

    if (results.length > 0) {
      const placeDetails = results[0];
      const latLng = await getLatLng(placeDetails);

      // Assuming you have a getTimeZoneByLocation function implemented
      const timezoneResponse = await getTimeZoneByLocation(latLng.lat, latLng.lng);
      latLng?.lat && latLng?.lng && getAddressStringByLatLong(latLng.lat, latLng.lng)
      // const time_zone = timezoneResponse?.timeZoneId;
      const time_zone = timezoneResponse;


      const formatted_address = placeDetails.formatted_address;

      const locationData: LocationData = {
        city: placeDetails.address_components.find(component => component.types.includes('locality'))?.long_name || '',
        state: placeDetails.address_components.find(component => component.types.includes('administrative_area_level_1'))?.long_name || '',
        zip: placeDetails.address_components.find(component => component.types.includes('postal_code'))?.long_name || '',
        street_number: placeDetails.address_components.find(component => component.types.includes('street_number'))?.short_name || '',
        street: placeDetails.address_components.find(component => component.types.includes('route'))?.long_name || '',
        address_line: formatted_address || '',
        longitude: latLng.lng || 0,
        latitude: latLng.lat || 0,
        time_zone: time_zone || '',
        formatted_address: formatted_address || '',
      };

      saveAddress(locationData);
      return locationData;
    } else {
      console.error('Error fetching location data: No results found for address:', cleanedAddress);
      // Try alternative address formats
      const alternativeFormats = [
        cleanedAddress.replace(/,/g, ''), // Remove commas
        cleanedAddress.replace(/\bUSA\b/i, '').trim(), // Remove USA
        cleanedAddress.split(',')[0], // Just the street address
      ];
      
      for (const altAddress of alternativeFormats) {
        if (altAddress !== cleanedAddress) {
          try {
            await waitForGoogleMaps();
            const altResults = await geocodeByAddress(altAddress);
            if (altResults.length > 0) {
              // Process the alternative result
              const placeDetails = altResults[0];
              const latLng = await getLatLng(placeDetails);
              const timezoneResponse = await getTimeZoneByLocation(latLng.lat, latLng.lng);
              const time_zone = timezoneResponse?.timeZoneId;
              const formatted_address = placeDetails.formatted_address;

              const locationData: LocationData = {
                city: placeDetails.address_components.find(component => component.types.includes('locality'))?.long_name || '',
                state: placeDetails.address_components.find(component => component.types.includes('administrative_area_level_1'))?.long_name || '',
                zip: placeDetails.address_components.find(component => component.types.includes('postal_code'))?.long_name || '',
                street_number: placeDetails.address_components.find(component => component.types.includes('street_number'))?.short_name || '',
                street: placeDetails.address_components.find(component => component.types.includes('route'))?.long_name || '',
                address_line: formatted_address || '',
                longitude: latLng.lng || 0,
                latitude: latLng.lat || 0,
                time_zone: time_zone || '',
                formatted_address: formatted_address || '',
              };

              saveAddress(locationData);
              return locationData;
            }
          } catch (altError) {
            // Alternative format also failed, continue to next format
          }
        }
      }
      
      // Set invalid response to trigger address popup
      const invalidAddress = {
        googleResponse: 'invalid'
      };
      localStorage.setItem("no-response", JSON.stringify(invalidAddress));
      return null;
    }
  } catch (error) {
    console.error('Error fetching location data for address:', address, error);
    // Set invalid response to trigger address popup
    const invalidAddress = {
      googleResponse: 'invalid'
    };
    localStorage.setItem("no-response", JSON.stringify(invalidAddress));
    showToastNotification('Unable to find the address. Please try a different address format.', 'Address Not Found', true);
    return null;
  }
};

const saveAddress = (address: LocationData) => {
  if (user?.phone_number) {
    // Dispatch your action to add new location data here
  } else {
    if (!isEmpty(address?.latitude)) {
      localStorage.setItem("address", JSON.stringify(address));
    }
  }
};

export default getAddressStringData;
